"use client";

import { type RefObject, useEffect, useRef, useState } from "react";
import { resolveTimeBasedRevealPercent } from "./waveform-bar-visual";

/** RAF-smoothed playhead from the element — avoids discrete `timeupdate` steps. */
export function useAudioElementProgressPercent(
	audioRef: RefObject<HTMLAudioElement | null>,
	enabled: boolean,
	fallbackPercent: number,
): number {
	const [percent, setPercent] = useState(fallbackPercent);

	useEffect(() => {
		if (!enabled) {
			setPercent(fallbackPercent);
			return;
		}

		let frame = 0;
		const tick = () => {
			const audio = audioRef.current;
			const duration = audio?.duration;
			if (
				audio &&
				duration != null &&
				Number.isFinite(duration) &&
				duration > 0
			) {
				setPercent(
					Math.max(
						0,
						Math.min(100, (audio.currentTime / duration) * 100),
					),
				);
			} else {
				setPercent(fallbackPercent);
			}
			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [audioRef, enabled, fallbackPercent]);

	return percent;
}

/** Eases toward a target percent — used to cascade buffer reveal left-to-right. */
export function useSmoothedPercent(
	targetPercent: number,
	enabled: boolean,
	easing = 0.12,
): number {
	const [value, setValue] = useState(0);

	useEffect(() => {
		if (!enabled) {
			setValue(0);
			return;
		}

		let frame = 0;
		const tick = () => {
			setValue((current) => {
				const delta = targetPercent - current;
				if (Math.abs(delta) < 0.15) {
					return targetPercent;
				}
				return current + delta * easing;
			});
			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [targetPercent, enabled, easing]);

	return value;
}

export interface StaggeredRevealPercentOptions {
	minTotalDurationMs?: number;
	/** Fired when the linear sweep clock starts (enabled + session active). */
	onSweepStart?: (startMs: number) => void;
}

/**
 * Linear display percent over minTotalDurationMs, capped by actual buffer.
 * At 1s of a 1s sweep → 100% revealed (when buffer allows); linear over minTotalDurationMs.
 */
export function useStaggeredRevealPercent(
	actualTargetPercent: number,
	enabled: boolean,
	sessionKey: number,
	options: StaggeredRevealPercentOptions = {},
): number {
	const { minTotalDurationMs = 1000, onSweepStart } = options;
	const [display, setDisplay] = useState(0);
	const actualRef = useRef(0);
	const sessionStartMsRef = useRef(0);

	useEffect(() => {
		actualRef.current = 0;
		setDisplay(0);
	}, [sessionKey]);

	useEffect(() => {
		if (!enabled) {
			actualRef.current = 0;
			setDisplay(0);
			return;
		}
		actualRef.current = Math.max(0, Math.min(100, actualTargetPercent));
	}, [actualTargetPercent, enabled]);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		// Start the sweep when the animation actually runs — not when the session
		// key is assigned while `enabled` is still false (dead time caused 0→100 jumps).
		sessionStartMsRef.current = performance.now();
		onSweepStart?.(sessionStartMsRef.current);
		setDisplay(0);

		let frame = 0;
		const tick = () => {
			const elapsed = performance.now() - sessionStartMsRef.current;
			const next = resolveTimeBasedRevealPercent(
				elapsed,
				actualRef.current,
				minTotalDurationMs,
			);
			setDisplay(next);
			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [enabled, minTotalDurationMs, sessionKey]);

	return display;
}

/** @deprecated Use useStaggeredRevealPercent for buffer reveal display. */
export function useBufferRevealPercent(
	targetPercent: number,
	enabled: boolean,
	sessionKey: number,
): number {
	return useStaggeredRevealPercent(targetPercent, enabled, sessionKey);
}

/** @deprecated Use waitForRevealDisplayCatchUp in voice-sample-prepare. */
export function waitForBufferRevealComplete(
	readDisplayPercent: () => number,
	readTargetPercent: () => number,
	maxFrames = 360,
): Promise<void> {
	return new Promise((resolve) => {
		let frames = 0;
		const tick = () => {
			if (
				readDisplayPercent() >= readTargetPercent() - 0.25 ||
				frames >= maxFrames
			) {
				resolve();
				return;
			}
			frames += 1;
			requestAnimationFrame(tick);
		};
		requestAnimationFrame(tick);
	});
}
